// Microsoft Teams account routes (/teams/accounts, Kjuup.io). Every call resolves to a plain result
// and never throws or toasts: the settings page maps the backend `code` to its own text. Same
// contract as the mobile app's redux/action/teamsApi.js.
//
//   { ok: true, ...fields }                       success
//   { ok: false, code, message, httpStatus }      failure (code may be null)
//
// callAPI never throws: it resolves to the response for every status (network errors become 500).
import { callAPI } from "./apiUtils";

const failure = (data, httpStatus, fallbackMessage) => ({
  ok: false,
  code: (data && typeof data.code === "string" && data.code) || null,
  message: (data && typeof data.message === "string" && data.message) || fallbackMessage || null,
  httpStatus: httpStatus || null,
});

async function call(method, url, body, params) {
  try {
    const verb = method === "get" ? "GET" : method === "put" ? "PUT" : "POST";
    const res = await callAPI(url, params || {}, verb, method === "get" ? null : body || {});
    const data = res && res.data;
    const status = res && res.status;
    if (!data || data.status === false || (status && status >= 400)) return failure(data, status);
    return { ok: true, data };
  } catch (e) {
    const res = e && e.response;
    if (res) return failure(res.data, res.status);
    return failure(null, null, (e && e.message) || null);
  }
}

/** GET /teams/accounts → { ok, enabled, accounts } (any number of Microsoft accounts, newest first). */
export async function listTeamsAccounts() {
  const r = await call("get", "/teams/accounts");
  if (!r.ok) return { ...r, enabled: false, accounts: [] };
  return { ok: true, enabled: !!r.data.enabled, accounts: Array.isArray(r.data.data) ? r.data.data : [] };
}

/**
 * GET /teams/accounts/auth-url?returnTo=<this origin> → { ok, url, expiresAt }. The backend sends
 * the sign-in result back to this origin (allowlisted origins only) instead of the app deep link.
 * `accountId` reconnects that account (its sign-in name pre-filled); without it the user picks any
 * Microsoft account, which is added next to the connected ones.
 */
export async function getTeamsAuthUrl({ accountId = null, calls = false } = {}) {
  const returnTo = typeof window !== "undefined" && window.location ? window.location.origin : undefined;
  // calls: the same sign-in, also allowing Teams call links (meeting permission).
  const params = { ...(returnTo ? { returnTo } : {}), ...(accountId != null ? { accountId } : {}), ...(calls ? { calls: 1 } : {}) };
  const r = await call("get", "/teams/accounts/auth-url", null, params);
  if (!r.ok) return r;
  if (!r.data.url) return failure(null, null);
  return { ok: true, url: r.data.url, expiresAt: r.data.expiresAt || null };
}

/** GET /teams/accounts/admin-consent-url → { ok, url, expiresAt } (the link for the IT admin of that account). */
export async function getTeamsAdminConsentUrl({ accountId = null } = {}) {
  const r = await call("get", "/teams/accounts/admin-consent-url", null, accountId != null ? { accountId } : {});
  if (!r.ok) return r;
  if (!r.data.url) return failure(null, null);
  return { ok: true, url: r.data.url, expiresAt: r.data.expiresAt || null };
}

/** POST /teams/accounts/complete { code } → { ok, account, message } (status 'connecting'). */
export async function completeTeamsConnect(code) {
  const r = await call("post", "/teams/accounts/complete", { code });
  if (!r.ok) return r;
  return { ok: true, account: r.data.data || null, message: r.data.message || null, code: r.data.code || "connected" };
}

/** GET /teams/accounts/:id/status → { ok, account } (account carries `jobs`). */
export async function getTeamsAccountStatus(id) {
  const r = await call("get", `/teams/accounts/${encodeURIComponent(id)}/status`);
  if (!r.ok) return r;
  return { ok: true, account: r.data.data || null };
}

/** POST /teams/accounts/:id/sync → { ok, code: 'queued' | 'already_queued', job }. */
export async function syncTeamsAccount(id) {
  const r = await call("post", `/teams/accounts/${encodeURIComponent(id)}/sync`);
  if (!r.ok) return r;
  return { ok: true, code: r.data.code || "queued", message: r.data.message || null, job: (r.data.data && r.data.data.job) || null };
}

/**
 * POST /teams/accounts/:id/disconnect { keepReadOnly } → { ok, account }.
 * keepReadOnly false (default) deletes the imported chats; true keeps them as a read-only archive.
 */
export async function disconnectTeamsAccount(id, { keepReadOnly = false } = {}) {
  const r = await call("post", `/teams/accounts/${encodeURIComponent(id)}/disconnect`, { keepReadOnly: !!keepReadOnly });
  if (!r.ok) return r;
  return { ok: true, account: r.data.data || null, message: r.data.message || null };
}

/**
 * PUT /teams/chats/:groupId/project { buildingId } → { ok, project, message }. Files the Teams chat
 * (its mirror group id) under a project room for the daily report; buildingId null clears it.
 * project: { id, name, themeColor } or null. Failure code 'room_not_assignable' for a room the user
 * cannot use (completed, deactivated, no longer a member).
 */
export async function setTeamsChatProject(groupId, buildingId) {
  const r = await call("put", `/teams/chats/${encodeURIComponent(groupId)}/project`, { buildingId: buildingId == null ? null : buildingId });
  if (!r.ok) return r;
  return { ok: true, project: r.data.project || null, message: r.data.message || null };
}

/**
 * GET /teams/projects/:buildingId/chats → the user's own Teams chats filed under this room ([] on
 * failure), newest activity first: { groupId, name, teamsChatType, webUrl, canWriteMessages,
 * teamsProject, lastMessage: { text, messageType, createdAt, senderIsMe, senderName } | null,
 * messageDate }.
 */
export async function listTeamsProjectChats(buildingId) {
  if (buildingId == null || buildingId === "") return [];
  const r = await call("get", `/teams/projects/${encodeURIComponent(buildingId)}/chats`);
  return r.ok && Array.isArray(r.data.chats) ? r.data.chats : [];
}

/** GET /email/classification/buildings → the rooms a chat or e-mail can be filed under ([] on failure). */
export async function listAssignableRooms() {
  const r = await call("get", "/email/classification/buildings");
  if (!r.ok || !Array.isArray(r.data.buildings)) return [];
  return r.data.buildings.map((b) => ({
    id: b.id,
    name: b.name || "",
    themeColor: b.themeColor || null,
    projectNo: b.projectNo || "",
    aliases: Array.isArray(b.aliases) ? b.aliases : [],
  }));
}

/** POST /teams/messages/:chatId/retry: a failed Teams media message is queued again. */
export async function retryTeamsDelivery(chatId) {
  if (!chatId) return { status: false };
  try {
    const res = await callAPI(`/teams/messages/${encodeURIComponent(chatId)}/retry`, {}, "POST", {});
    return (res && res.data) || { status: false };
  } catch (error) {
    return (error && error.response && error.response.data) || { status: false };
  }
}

/**
 * GET /teams/people?q= → { ok, people, directory }. People for a new Teams chat: the user's Teams
 * chat partners first, then (2+ characters) the Microsoft directory of their organisation.
 * person: { aadUserId, displayName, email, jobTitle }. directory: 'ok' | 'skipped' | 'forbidden'
 * (the organisation does not allow the directory search) | 'failed'. `accountId` picks one of
 * several connected Microsoft accounts (default: the newest).
 */
export async function searchTeamsPeople(q, { accountId = null } = {}) {
  const r = await call("get", "/teams/people", null, { q: q || "", ...(accountId != null ? { accountId } : {}) });
  if (!r.ok) return { ...r, people: [], directory: "failed" };
  return { ok: true, people: Array.isArray(r.data.people) ? r.data.people : [], directory: r.data.directory || "ok" };
}

/**
 * POST /teams/chats { members, topic, buildingId, accountId } → { ok, groupId, chatType, project, message }.
 * Creates the chat in Microsoft Teams as account `accountId` (default: the newest connected one; one
 * person = 1:1, Teams reuses an existing 1:1; more = group chat with an optional topic) and its
 * Kjuup mirror; buildingId files it under that room.
 * Failure codes: no_members, too_many_members, room_not_assignable, chat_forbidden, chat_failed,
 * not_connected, not_entitled.
 */
export async function createTeamsChat({ members, topic = null, buildingId = null, accountId = null }) {
  const r = await call("post", "/teams/chats", {
    members: Array.isArray(members) ? members : [],
    topic: topic || null,
    buildingId: buildingId == null ? null : buildingId,
    ...(accountId != null ? { accountId } : {}),
  });
  if (!r.ok) return r;
  if (!r.data.groupId) return failure(null, null);
  return {
    ok: true,
    groupId: String(r.data.groupId),
    chatType: r.data.chatType === "oneOnOne" ? "oneOnOne" : "group",
    project: r.data.project || null,
    message: r.data.message || null,
  };
}

/**
 * POST /teams/chats/:groupId/call { video } → { ok, joinUrl, video, accountId }. Creates a Teams
 * meeting as the chat's Microsoft account; the caller sends the link into the chat.
 * Failure codes (with accountId): call_consent_required (409: the account has not allowed meetings
 * yet → getTeamsAuthUrl({ accountId, calls: true })), call_forbidden, call_failed, auth_error,
 * teams_reconnect, teams_read_only, not_entitled.
 */
export async function createTeamsCallLink(groupId, { video = false } = {}) {
  try {
    const res = await callAPI(`/teams/chats/${encodeURIComponent(groupId)}/call`, {}, "POST", { video: !!video });
    const data = res && res.data;
    const status = res && res.status;
    const accountId = data && data.accountId != null ? String(data.accountId) : null;
    if (!data || data.status === false || (status && status >= 400) || !data.joinUrl) {
      return { ...failure(data, status), accountId };
    }
    return { ok: true, joinUrl: String(data.joinUrl), video: !!data.video, accountId };
  } catch (e) {
    const res = e && e.response;
    return { ...failure(res && res.data, res && res.status, (e && e.message) || null), accountId: null };
  }
}

/**
 * POST /teams/bridges { groupId, accountId, members } → { ok, bridge }. Gives a new native Kjuup
 * group (a combined Kjuup + Teams chat) its Microsoft Teams copy with the picked Teams people
 * (members: [{ aadUserId, displayName, email }], see utils/teams/combinedChat buildBridgeRequest).
 * bridge: { groupId, webUrl, guests: [{ id, fullName }], kjuupInTeams, labelOnly }.
 * Failure codes: teams_not_connected, teams_forbidden, too_many_members, not_group_member,
 * bad_request, failed.
 */
export async function createTeamsBridge({ groupId, accountId = null, members }) {
  const r = await call("post", "/teams/bridges", {
    groupId: groupId == null ? null : String(groupId),
    ...(accountId != null && accountId !== "" ? { accountId: String(accountId) } : {}),
    members: Array.isArray(members) ? members : [],
  });
  if (!r.ok) return r;
  return { ok: true, bridge: r.data.bridge || null };
}
